import { FC, useMemo, useCallback, useEffect } from 'react'

import { List, Pagination, EmptyList, SafetyCaseCard } from '../../../components'
import { ISafetyCase, IRefs } from '../../../interfaces'

type SafetyCasesCardsListProps = {
  pagination: { pageSize: number; pageIndex: number }
  setPagination: (values: { [key: string]: number }) => void
  safetyCases: ISafetyCase[] | undefined
  isSafetyCasesInitialLoading: boolean
  safetyCasesRefs: IRefs
  activeSafetyCaseId?: number
  isSafetyCasesLoading: boolean
  handleNavigateToSafetyCase: (safetyCase: ISafetyCase) => void
  onLocationClick?: (safetyCase: ISafetyCase) => void
}

const SafetyCasesCardsList: FC<SafetyCasesCardsListProps> = props => {
  const {
    safetyCases,
    isSafetyCasesInitialLoading,
    pagination,
    setPagination,
    safetyCasesRefs,
    activeSafetyCaseId,
    isSafetyCasesLoading,
    handleNavigateToSafetyCase,
    onLocationClick,
  } = props

  const totalItemsCount = safetyCases?.length || 0

  const { pageIndex, pageSize } = pagination

  const setPageSize = useCallback((pageSize: number) => setPagination({ pageSize }), [setPagination])

  const setPageIndex = useCallback((pageIndex: number) => setPagination({ pageIndex }), [setPagination])

  const totalPageCount = useMemo(() => Math.ceil(totalItemsCount / pageSize), [totalItemsCount, pageSize])

  const safetyCasesPage = useMemo(
    () => (safetyCases || []).slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [safetyCases, pageIndex, pageSize],
  )

  useEffect(() => {
    if (pageIndex > totalPageCount) {
      setPageIndex(totalPageCount)
    }
  }, [pageIndex, setPageIndex, totalPageCount])

  const canNextPage = pageIndex < totalPageCount - 1

  const canPreviousPage = pageIndex > 0

  return (
    <>
      <div className="overflow-auto h-100">
        <List
          data={safetyCasesPage}
          ListItem={SafetyCaseCard}
          isInitialLoading={isSafetyCasesInitialLoading}
          noDataContent={<EmptyList>No Safety Cases found.</EmptyList>}
          activeId={activeSafetyCaseId}
          refs={safetyCasesRefs}
          onTitleClick={handleNavigateToSafetyCase}
          onLocationClick={onLocationClick}
          isLoading={isSafetyCasesLoading}
        />
      </div>

      <Pagination
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItemsCount={totalItemsCount}
        totalPageCount={totalPageCount}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
      />
    </>
  )
}

export default SafetyCasesCardsList
